import { Download } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { trackCTA } from "@/lib/tracking";

const SECTIONS: { h: string; body: string[] }[] = [
  {
    h: "1. Scope of Services",
    body: [
      "FSAP provides advocacy, strategy, representation, accompaniment, and systems navigation as scoped in the client's engagement addendum. Services are delivered by Dr. Sully or authorized FSAP Care Team members.",
      "Scope is bounded by the tier selected (Foundation, Priority, Elite, or Compassion Plan) or the à la carte engagement described in the addendum.",
    ],
  },
  {
    h: "2. Non-Legal / Non-Medical / Non-Therapy Disclaimer",
    body: [
      "FSAP is not a law firm and does not provide legal advice. Where legal representation is required, clients are referred to licensed Texas attorneys.",
      "FSAP does not provide medical advice, diagnosis, therapy, or mental-health treatment. Where clinical services are needed, clients are referred to licensed providers.",
    ],
  },
  {
    h: "3. No-Guarantee Clause",
    body: [
      "FSAP improves preparation, presence, and process. No specific outcome — educational, legal, medical, or administrative — is promised or implied.",
    ],
  },
  {
    h: "4. Fees & Payment Terms",
    body: [
      "Premium retainers are billed monthly in advance. À la carte services are billed upon booking. Compassion Plan fees are billed monthly in advance at the reduced rate.",
      "Sliding scale, scholarships, and fee waivers require written approval and documentation of need. Approved reductions are recorded in the engagement addendum with clear duration and scope, and may be time-limited or tied to specific events or services. Approval may be revoked or renegotiated if circumstances change.",
      "Payment is due by the 1st of the month for retainers. Late fees may apply after 10 days.",
    ],
  },
  {
    h: "5. Scheduling & Cancellation",
    body: [
      "Meeting representation and accompaniment require 5 business days advance scheduling when possible. Emergency scheduling is subject to availability and after-hours rates.",
      "Client cancellations within 48 hours of a scheduled meeting may be billed at 50%. No-shows are billed in full.",
    ],
  },
  {
    h: "6. Transportation & Accompaniment Boundaries",
    body: [
      "FSAP does not provide transportation of clients or family members. Accompaniment is defined as meeting-side presence and does not include personal care, medical assistance, or supervision of minors outside the meeting.",
    ],
  },
  {
    h: "7. Confidentiality & Mandated Reporting",
    body: [
      "Case information is held in confidence, shared only with parties named in the engagement addendum or as required by law.",
      "FSAP complies with Texas mandated-reporting law. Suspected abuse, neglect, or exploitation of a child, elder, or person with a disability will be reported to the appropriate authority.",
    ],
  },
  {
    h: "8. Communication Boundaries",
    body: [
      "Email is the primary channel. Response windows follow the selected tier. After-hours communication is for case-relevant urgent windows only, not general questions.",
      "FSAP does not act as a 24/7 crisis line. Genuine emergencies require 911, Texas 988, or the appropriate crisis service.",
    ],
  },
  {
    h: "9. Termination",
    body: [
      "Either party may terminate with 30 days written notice. Immediate termination is available for material breach (non-payment, misrepresentation, threats to staff, or requests that violate law or ethics).",
      "On termination, unused retainer time within the current cycle is not refunded; work already scheduled is completed or transitioned in writing.",
    ],
  },
  {
    h: "10. Signature",
    body: [
      "By signing below, the client (or guardian) acknowledges receipt of this agreement, understands the boundaries and disclaimers, and authorizes FSAP to begin the engagement described in the addendum.",
    ],
  },
];

const AdvocacyAgreement = () => (
  <div className="min-h-screen bg-bg-primary text-fg-primary">
    <PageMeta
      title="FSAP Service Agreement (Draft) | Dr. Sully"
      description="Working draft of the Full-Scope Advocacy Partner service agreement covering scope, non-legal/non-medical disclaimers, fees, sliding-scale approvals, and boundaries."
      path="/advocacy/agreement"
      noindex
    />
    <SiteNav />
    <main>
      <section className="border-b border-fg-muted/10 bg-bg-secondary">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <p className="font-display uppercase tracking-[0.24em] text-gold text-xs mb-3">Service Agreement · DRAFT</p>
          <h1 className="font-display text-4xl md:text-5xl leading-tight mb-4">
            Full-Scope Advocacy Partner — Service Agreement
          </h1>
          <p className="text-fg-secondary max-w-2xl">
            This is a working DRAFT for attorney review before signature. Do not sign this document without a
            licensed Texas attorney reviewing the fee, liability, mandated-reporting, and termination sections.
          </p>
          <a
            href="/docs/FSAP_Service_Agreement_Draft.pdf"
            onClick={() => trackCTA("fsap_agreement_download", { location: "agreement_page" })}
            className="mt-6 inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.18em] text-xs px-6 py-3.5 hover:brightness-110"
          >
            Download PDF <Download className="h-4 w-4" />
          </a>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 py-16 space-y-10">
        {SECTIONS.map((s) => (
          <section key={s.h}>
            <h2 className="font-display text-2xl mb-3">{s.h}</h2>
            {s.body.map((p, i) => (
              <p key={i} className="text-sm text-fg-secondary mb-3 leading-relaxed">{p}</p>
            ))}
          </section>
        ))}

        <section className="border-t border-fg-muted/15 pt-8">
          <h2 className="font-display text-2xl mb-6">Signatures</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="border-b border-fg-muted/40 h-14" />
              <div className="text-xs uppercase tracking-[0.14em] text-fg-muted mt-2">Client / Guardian · Date</div>
            </div>
            <div>
              <div className="border-b border-fg-muted/40 h-14" />
              <div className="text-xs uppercase tracking-[0.14em] text-fg-muted mt-2">FSAP · Date</div>
            </div>
          </div>
        </section>
      </article>
    </main>
    <SiteFooter />
  </div>
);

export default AdvocacyAgreement;
